/**
 * Size Chart Landing Page JavaScript
 * Handles deep linking for tabs and the dynamic Boards size chart
 */

/**
 * Board Size Chart Custom Element
 * Handles gender/board selection and displays the size chart
 */
class BoardSizeChart extends HTMLElement {
  constructor () {
    super()
    this.boards = []
  }

  connectedCallback () {
    this.genderSelect = this.querySelector(
      '[data-board-size-chart-el="gender"]',
    )
    this.boardSelect = this.querySelector('[data-board-size-chart-el="board"]')
    this.chartContainer = this.querySelector(
      '[data-board-size-chart-el="chart"]',
    )

    this.handleGenderChange = this.handleGenderChange.bind(this)
    this.handleBoardChange = this.handleBoardChange.bind(this)

    this.genderSelect?.addEventListener('change', this.handleGenderChange)
    this.boardSelect?.addEventListener('change', this.handleBoardChange)

    this.loadBoardData()
  }

  loadBoardData () {
    const dataScript = this.querySelector('[data-board-size-chart-data]')
    if (dataScript) {
      try {
        this.boards = JSON.parse(dataScript.textContent)
      } catch (e) {
        console.error('Failed to parse board data:', e)
        this.boards = []
      }
    }
  }

  handleGenderChange (event) {
    const gender = event.target.value

    // Reset board select
    this.boardSelect.innerHTML = `<option value="">${this.boardSelect.dataset.placeholder || 'Select Board'}</option>`

    if (!gender) {
      this.boardSelect.disabled = true
      this.renderPrompt()
      return
    }

    // Filter boards by gender
    // Map dropdown values to possible gender codes
    const genderMap = {
      mens: ['m', 'mens', 'men', 'male', "men's"],
      womens: ['w', 'womens', 'women', 'female', "women's"],
      kids: ['k', 'kids', 'kid', 'youth', 'grom', "kid's", "kids'"],
    }
    const validCodes = genderMap[gender] || []

    // If board has no gender data or is unisex, show for all genders
    const filteredBoards = this.boards.filter((board) => {
      const boardGender = (board.gender || '')
        .toLowerCase()
        .replace(/['']/g, "'")
      if (!boardGender || boardGender === 'u' || boardGender === 'unisex') {
        return true
      }
      return validCodes.includes(boardGender)
    })

    if (filteredBoards.length === 0) {
      this.boardSelect.disabled = true
      this.renderNoBoards()
      return
    }

    // Sort boards alphabetically
    filteredBoards.sort((a, b) => a.title.localeCompare(b.title))

    // Populate board select
    filteredBoards.forEach((board) => {
      const option = document.createElement('option')
      option.value = board.handle
      option.textContent = board.title
      this.boardSelect.appendChild(option)
    })

    this.boardSelect.disabled = false
    this.renderPrompt()
  }

  handleBoardChange (event) {
    const boardHandle = event.target.value

    if (!boardHandle) {
      this.renderPrompt()
      return
    }

    const board = this.boards.find((b) => b.handle === boardHandle)
    if (!board) {
      this.renderError()
      return
    }

    this.renderSizeChart(board)
  }

  renderPrompt () {
    const promptText =
      this.dataset.selectPrompt ||
      'Select a gender and board to view the size chart.'
    this.chartContainer.innerHTML = `
      <p class="pg-SizeChartLanding_BoardPrompt fz-16_140">
        ${this.escapeHtml(promptText)}
      </p>
    `
  }

  renderNoBoards () {
    const noBoardsText =
      this.dataset.noBoards || 'No boards available for the selected gender.'
    this.chartContainer.innerHTML = `
      <p class="pg-SizeChartLanding_BoardPrompt fz-16_140">
        ${this.escapeHtml(noBoardsText)}
      </p>
    `
  }

  renderError () {
    const template = this.querySelector(
      '[data-board-size-chart-el="error-template"]',
    )
    if (template) {
      this.chartContainer.innerHTML = template.innerHTML
    } else {
      this.chartContainer.innerHTML = `
        <p class="pg-SizeChartLanding_Error fz-16_140">
          Size chart not available for this board.
        </p>
      `
    }
  }

  renderSizeChart (board) {
    const sizeChart = board.size_chart
    const variants = board.variants || []

    // Try metaobject size chart first
    if (sizeChart && (sizeChart.cm_guide || sizeChart.inch_guide)) {
      this.renderMetaobjectChart(board.title, sizeChart)
      return
    }

    // Fall back to variant-based size chart
    const variantsWithData = variants.filter(
      (v) => v.size && (v.weight_range || v.waist_width || v.effective_edge),
    )

    if (variantsWithData.length > 0) {
      this.renderVariantChart(board.title, variantsWithData)
      return
    }

    // No data available
    this.renderError()
  }

  renderMetaobjectChart (title, sizeChart) {
    const hasBoth = sizeChart.cm_guide && sizeChart.inch_guide
    const initialSize = 'in' // Default to inches

    let html = `
      <size-guide class="pg-SizeChartLanding_ChartContent" current-size="${initialSize}">
        <div class="pg-SizeChartLanding_ChartHeader">
          <h3 class="pg-SizeChartLanding_ChartTitle fz-20_130">${this.escapeHtml(sizeChart.title || title)} Size Chart</h3>
    `

    // Add toggle if both units available
    if (hasBoth) {
      html += `
          <div class="pg-SizeChartLanding_ChartSwitcher" data-size-guide-el="trigger">
            <span class="pg-SizeChartLanding_ChartSwitcherLabel pg-SizeChartLanding_ChartSwitcherLabel-cm fz-14_140">
              Cm
            </span>
            <button type="button" class="pg-SizeChartLanding_ChartSwitch">
              <span class="pg-SizeChartLanding_ChartSlider"></span>
            </button>
            <span class="pg-SizeChartLanding_ChartSwitcherLabel pg-SizeChartLanding_ChartSwitcherLabel-in fz-14_140">
              Inch
            </span>
          </div>
      `
    }

    html += `
        </div>
        <div class="pg-SizeChartLanding_ChartBody">
    `

    if (sizeChart.cm_guide) {
      const cmContent = sizeChart.cm_guide
        .replace(/<table/g, '<div class="rte-RichText_Table"><table')
        .replace(/<\/table>/g, '</table></div>')
      const cmClass = hasBoth ? ' pg-SizeChartLanding_Table-cm' : ''
      html += `<div class="pg-SizeChartLanding_Table${cmClass} rte-RichText">${cmContent}</div>`
    }

    if (sizeChart.inch_guide) {
      const inchContent = sizeChart.inch_guide
        .replace(/<table/g, '<div class="rte-RichText_Table"><table')
        .replace(/<\/table>/g, '</table></div>')
      const inchClass = hasBoth ? ' pg-SizeChartLanding_Table-in' : ''
      html += `<div class="pg-SizeChartLanding_Table${inchClass} rte-RichText">${inchContent}</div>`
    }

    html += `
        </div>
    `

    if (sizeChart.text) {
      html += `<p class="pg-SizeChartLanding_ChartText fz-12_150">${this.escapeHtml(sizeChart.text)}</p>`
    }

    html += `</size-guide>`
    this.chartContainer.innerHTML = html
  }

  renderVariantChart (title, variants) {
    const rows = [
      { key: 'weight_range', label: 'Rider Weight' },
      { key: 'waist_width', label: 'Waist Width' },
      { key: 'stance_width', label: 'Stance Width' },
      { key: 'stance_location', label: 'Stance Setback' },
      { key: 'binding_size', label: 'Binding Size' },
      { key: 'effective_edge', label: 'Effective Edge' },
      { key: 'running_length', label: 'Running Length' },
      { key: 'sidecut_radius', label: 'Sidecut Radius' },
    ]

    // Filter to rows that have data
    const visibleRows = rows.filter((row) =>
      variants.some((v) => v[row.key] && v[row.key] !== ''),
    )

    let html = `
      <div class="pg-SizeChartLanding_ChartWrapper">
        <h3 class="pg-SizeChartLanding_ChartTitle fz-20_130">${this.escapeHtml(title)} Size Chart</h3>
        <div class="rte-RichText">
          <div class="rte-RichText_Table">
            <table>
              <thead>
                <tr>
                  <th></th>
                  ${variants.map((v) => `<th>${this.escapeHtml(v.size || v.title)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
    `

    visibleRows.forEach((row) => {
      html += `
        <tr>
          <td>${row.label}</td>
          ${variants.map((v) => `<td>${this.escapeHtml(v[row.key] || '-')}</td>`).join('')}
        </tr>
      `
    })

    html += `
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `

    this.chartContainer.innerHTML = html
  }

  escapeHtml (str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}

if (!customElements.get('board-size-chart')) {
  customElements.define('board-size-chart', BoardSizeChart)
}

/**
 * Size Chart Landing Deep Linking
 * Handles URL hash navigation for tabs
 */
class SizeChartLandingDeepLink {
  constructor () {
    this.tabsElement = document.querySelector(
      '[data-size-chart-landing-el="tabs"]',
    )
    if (!this.tabsElement) return

    this.init()
  }

  init () {
    // Handle initial hash on page load
    this.handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', () => this.handleHashChange())

    // Update hash when tab is clicked
    this.tabsElement
      .querySelectorAll('[data-tabs-list-el="tab"]')
      .forEach((tab) => {
        tab.addEventListener('click', (e) => this.handleTabClick(e))
      })
  }

  handleHashChange () {
    let hash = window.location.hash.slice(1) // Remove the #
    if (!hash) return

    // Normalize hash: step-on -> stepon (tab id uses stepon)
    if (hash === 'step-on') {
      hash = 'stepon'
    }

    const tabId = `tab-${hash}`
    const tab = this.tabsElement.querySelector(`#${tabId}`)

    if (tab && tab.getAttribute('aria-selected') !== 'true') {
      tab.click()
    }
  }

  handleTabClick (event) {
    const tab = event.target.closest('[data-tabs-list-el="tab"]')
    if (!tab) return

    const tabId = tab.id
    const hash = tabId.replace('tab-', '')

    // Update URL without adding to history
    history.replaceState(null, '', `#${hash}`)
  }
}

// Initialize deep linking when DOM is ready
function initSizeChartDeepLink () {
  const deepLink = new SizeChartLandingDeepLink()
  return deepLink
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeChartDeepLink)
} else {
  initSizeChartDeepLink()
}
